import { createAsyncThunk } from "@reduxjs/toolkit";
import { clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";
import { createSlice } from "@reduxjs/toolkit";

// Initial state structure for the Redux store
const INIT_STATE = {
    instances: {}
}

const route = "modules/.common/components/FilterAvatars/redux/slice";

// GraphQL query for searching drivers
const SEARCH_DRIVERS_QUERY = `
    query searchDrivers ($conditions: [DriverSearchInput], $sort: [DriverSortInput], $limit: Int, $offset: Int) {
        data: searchDrivers (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
            items {
                id
                externalId
                authId
                title
                name
                tagIds
                driverId
                companyId
                contactInfo
                idTimeZone
                status
                driverScheduledTasksCount
                avatarFile {
                    id
                    fileKey
                }
                permissions
                dateformat
                supervisorId
            }
            total
        }
    }
`;

// GraphQL query for searching avatars
const SEARCH_AVATAR_QUERY = `query($fileKey: String!, $timeout: Int) {
    data: getSecureFile(fileKey: $fileKey, timeout: $timeout)
}`;

/**
 * Async thunk to search for drivers with pagination and filtering
 * @param {Object} params - Search parameters
 * @param {string} params.instanceId - Unique ID for the component instance
 * @param {Array} params.conditions - Search conditions
 * @param {Array} params.sort - Sorting criteria
 * @param {number} params.limit - Number of results per page
 * @param {number} params.offset - Offset for pagination
 */
export const getDriversSearch = createAsyncThunk(
    "workOrders/getDriversSearch",
    async ({ instanceId, conditions, sort, limit, offset }, { rejectWithValue }) => {
        try {
            const responseDrivers = await clientQuery(
                SEARCH_DRIVERS_QUERY,
                { conditions, sort: [
                    { field: "title", order: "ASC" }
                ], limit, offset },
                endpoints.GRAPHQL_GENERAL_V2
            );
            
            if (!responseDrivers?.data) {
                throw new Error("Invalid response format from server");
            }

            if(responseDrivers?.data){
                const driversWithAvatars = await Promise.all(
                    responseDrivers.data.items.map(async (driver) => {
                        // Create a copy of the driver object to avoid mutating immutable objects
                        const driverCopy = { ...driver };
                        
                        if (driver.avatarFile?.fileKey) {
                            try {
                                const avatarResponse = await clientQuery(
                                    SEARCH_AVATAR_QUERY,
                                    { fileKey: driver.avatarFile.fileKey, timeout: 600 },
                                    endpoints.GRAPHQL_GENERAL_V2
                                );
                                if (avatarResponse?.data) {
                                    driverCopy.avatarUrl = avatarResponse.data;
                                } else {
                                    driverCopy.avatarUrl = null;
                                }
                            } catch (error) {
                                driverCopy.avatarUrl = null;
                            }
                        } else {
                            driverCopy.avatarUrl = null;
                        }
                        return driverCopy;
                    })
                );
                
                // Create a copy of the data object to avoid mutating immutable objects
                const updatedData = {
                    ...responseDrivers.data,
                    items: driversWithAvatars
                };
                
                return { instanceId, data: updatedData };
            }
            
            return { instanceId, data: responseDrivers.data };
        } catch (error) {
            console.error("getDriversSearch - Error:", error);
            ExceptionManager(error, route, "getDriversSearch");
            return rejectWithValue({ 
                instanceId, 
                error: error.message || "Failed to fetch drivers" 
            });
        }
    }
)

// Create the Redux slice
const filterAvatarsSlice = createSlice({
    name: "filterAvatars",
    initialState: INIT_STATE,
    reducers: {
        /**
         * Initialize a new instance in the Redux store
         */
        initializeInstance: (state, action) => {
            const { instanceId } = action.payload;
            if (!state.instances[instanceId]) {
                state.instances[instanceId] = {
                    totalDrivers: 0
                };
            }
        },
        /**
         * Remove an instance from the Redux store
         */
        removeInstance: (state, action) => {
            const { instanceId } = action.payload;
            if (state.instances[instanceId]) {
                delete state.instances[instanceId];
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getDriversSearch.fulfilled, (state, action) => {
            const { instanceId, data } = action.payload;
            if (state.instances[instanceId]) {
                if (!state.instances[instanceId].totalDrivers) {
                    state.instances[instanceId].totalDrivers = data?.total ?? 0;
                }
            }
        });
    }
})

export const { actions, reducer } = filterAvatarsSlice;

export const { reset, initializeInstance, removeInstance } = actions;

export default reducer;